import Router from "next/router"

import createMyShoppingList from "@secureo/common/utils/commerceTools/graphql/mutations/createMyShoppingList"
import updateMyShoppingList from "@secureo/common/utils/commerceTools/graphql/mutations/updateMyShoppingList"
import getShoppingListDraftFromLockingSystemReducerState, {
	getLockingSystemShoppingListCustomFieldsDraft,
	getTextLineItemDraftsFromComponentsByUuid,
} from "../utils/getShoppingListDraftFromLockingSystemReducerState"
import fetchCommerceToolsGraphQLApi from "@secureo/common/utils/commerceTools/graphql/fetchCommerceToolsGraphQLApi"

import {
	ShoppingList,
	MyShoppingListUpdateAction,
	RemoveShoppingListTextLineItem,
	ChangeShoppingListName,
	// SetShoppingListDescription,
} from "@secureo/common/typings/generated/commercetools-graphql"
import { DispatchLockingSystemReducer, LockingSystemReducerState } from ".."
import { CurrencyCode } from "@secureo/common/typings/Forex"

type SaveLockingSystemPayload = Pick<
	LockingSystemReducerState,
	"hasUnsavedChanges" | "shoppingListId" | "shoppingListVersion" | "isLoading" | "isFrozen"
>

const query = /* GraphQL */ `
	query shoppingLists($id: String!) {
		me {
			shoppingList(id: $id) {
				id
				version
				textLineItems {
					id
					quantity
				}
			}
		}
	}
`

export const saveLockingSystemConfiguration = async (
	accessToken: string,
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	currencyCode: CurrencyCode,
) => {
	dispatch({
		type: "SET_IS_LOADING",
		payload: true,
	})

	let shoppingListId = reducerState.shoppingListId
	let shoppingListVersion = reducerState.shoppingListVersion

	try {
		if (shoppingListId) {
			// Update shoppingList
			const {
				me: { shoppingList },
			} = (await fetchCommerceToolsGraphQLApi(accessToken, query, {
				id: shoppingListId,
			})) as {
				me: {
					shoppingList: ShoppingList
				}
			}

			const { version, textLineItems } = shoppingList

			// Remove all components
			const removeComponentActions: MyShoppingListUpdateAction[] = textLineItems.map(
				(textLineItems) => {
					const removeTextLineItem: RemoveShoppingListTextLineItem = {
						textLineItemId: textLineItems.id,
						quantity: textLineItems.quantity,
					}

					return { removeTextLineItem }
				},
			)

			// Add all components
			const textLineItemDrafts = getTextLineItemDraftsFromComponentsByUuid(
				reducerState.componentsByUuid,
			)
			const addComponentActions: MyShoppingListUpdateAction[] = textLineItemDrafts.map(
				(textLineItemDraft) => {
					return {
						addTextLineItem: textLineItemDraft,
					}
				},
			)

			// Change name
			const changeName: ChangeShoppingListName = {
				name: [
					{
						locale: "de",
						value: reducerState.name,
					},
				],
			}

			// Set customFields
			const customFieldsDraft = getLockingSystemShoppingListCustomFieldsDraft(reducerState)
			const setCustomFieldActions: MyShoppingListUpdateAction[] =
				customFieldsDraft.fields.map((customFieldInput) => {
					return {
						setCustomField: customFieldInput,
					}
				})

			const actions: MyShoppingListUpdateAction[] = [
				...removeComponentActions,
				...addComponentActions,
				{ changeName },
				...setCustomFieldActions,
			]

			const { version: updatedVersion } = await updateMyShoppingList(
				accessToken,
				shoppingListId,
				version,
				actions,
				currencyCode,
			)
			shoppingListVersion = updatedVersion
		} else {
			// Create new shoppingList
			const draft = getShoppingListDraftFromLockingSystemReducerState(reducerState)

			const shoppingList = await createMyShoppingList(accessToken, draft, "EUR")
			shoppingListId = shoppingList.id
			shoppingListVersion = shoppingList.version

			// Save lockingSystemId in url
			const as = `${window.location.href?.split("?")?.[0]}?id=${shoppingListId}`
			Router.push("/[locale]/locking-system/configure/[slug]", as, { shallow: true })
		}

		const payload: SaveLockingSystemPayload = {
			hasUnsavedChanges: false,
			shoppingListId,
			shoppingListVersion,
			isLoading: false,
			isFrozen: false,
		}

		dispatch({
			type: "SAVE_LOCKING_SYSTEM",
			payload,
		})

		return shoppingListId
	} catch (err) {
		console.error(err)

		dispatch({
			type: "SET_IS_LOADING",
			payload: false,
		})
	}
}
